import {
  AppShellRailTile,
  Badge,
  Box,
  Button,
  Icon,
  Menu,
  MenuGroup,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuShortcut,
  useKeyboardHints,
  MenuTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import React from "react";

import { AboutDialog } from "#app/components/navigation/about-dialog";
import { EntityTag, useEntityDetail } from "#app/components/entity";
import { FeedbackDialog } from "#app/components/navigation/feedback-dialog";
import { HelpSubMenu } from "#app/components/navigation/help-sub-menu";
import { LogoutConfirmDialog } from "#app/components/auth/logout-confirm-dialog";
import { ThemeSubMenu } from "#app/components/navigation/theme-sub-menu";
import { readJWT } from "#app/lib/jwt";
import { useAnnouncements } from "#app/hooks/use-announcements";
import { useAppData } from "#app/lib/data";
import { useAuth } from "#app/lib/auth-context";
import { KEYBOARD_SHORTCUTS_SHORTCUT_LABEL } from "#app/components/keyboard-shortcuts/keyboard-shortcuts-catalog";
import { LOGOUT_SHORTCUT_LABEL } from "#app/lib/hooks/use-logout-hotkey";
import { useAdminDialogActions } from "#app/lib/admin-dialog-context";
import { useKeyboardShortcutsDialogActions } from "#app/lib/keyboard-shortcuts-dialog-context";
import { useWhatsNewDialogActions } from "#app/lib/whats-new-dialog-context";
import { useCanOpenTrash } from "#app/components/settings/moved-to-trash-title";
import { useSettingsDoors } from "./use-settings-doors";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useCloseSidebarIfCompact } from "#app/lib/mobile-sidebar-context";
import {
  resolveEntityLabel,
  type EntityData,
} from "#app/lib/entity-visuals";

/**
 * The account menu — the viewer's own identity and every account-scoped
 * action (profile, settings, theme, what's new, help, trash, log out).
 * It owns the dialogs those rows open, so wherever the trigger is mounted the
 * whole surface comes with it.
 *
 * Two hosts, one menu: `rail` is the module rail's pinned bottom item on
 * desktop, `header` is the mobile nav drawer's top-right control. Only the
 * trigger chrome and the popup's anchor side differ.
 */
export function AccountMenu({
  variant = "header",
}: {
  variant?: "header" | "rail";
}) {
  const { currentUser } = useAppData();
  const { openEntity } = useEntityDetail();
  const { state: authState } = useAuth();
  const { push: navigate } = useAppNavigate();
  const { enterSettings } = useSettingsDoors();
  // The profile is the shell's right island, which the compact nav drawer
  // (this menu's `header` host) covers whole — the drawer has to get out of
  // the way for the island to show. No-op on the other tiers.
  const closeDrawer = useCloseSidebarIfCompact();

  const isSuperuser = authState.accessToken
    ? ((readJWT(authState.accessToken).superuser as boolean) ?? false)
    : false;
  // Trash is self-serve (the page scopes non-managers to their own
  // deletions), so the door opens for anyone who can put something in it.
  const canOpenTrash = useCanOpenTrash();
  const { openDialog: openKeyboardShortcuts } = useKeyboardShortcutsDialogActions();
  const keyboardHints = useKeyboardHints();
  const { openDialog: openAdmin } = useAdminDialogActions();

  // The dialog itself is mounted once in the workspace layout
  // (WhatsNewDialogMount) — this menu only opens it and shows the count.
  const { unseenCount } = useAnnouncements();
  const { openDialog: openWhatsNew } = useWhatsNewDialogActions();

  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [aboutOpen, setAboutOpen] = React.useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = React.useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);

  const accountEntity: EntityData = {
    kind: "user",
    id: currentUser?.userId ?? "me",
    user: currentUser ?? null,
  };

  return (
    <>
      <Menu onOpenChange={setAccountMenuOpen} open={accountMenuOpen}>
        {/* Both triggers word the avatar the same way every who-surface does:
          * resolveEntityLabel falls back name → email for a not-yet-onboarded
          * account. */}
        {variant === "rail" ? (
          // Rail: the account rides the same 32px chip every rail tile wears, so
          // the column reads as one stack of equal marks. The label is the
          // tooltip only — an identity tile is never captioned, in either rail
          // mode.
          <Tooltip>
            <TooltipTrigger
              render={
                <MenuTrigger
                  render={
                    <AppShellRailTile
                      compact
                      label="Account menu"
                      visual={
                        <EntityTag
                          // Fills the chip, like the workspace mark — see
                          // WorkspaceRailTile.
                          avatarSize="default"
                          display="visual"
                          entity={accountEntity}
                          interactive={false}
                          presence
                          size="md"
                        />
                      }
                    />
                  }
                />
              }
            />
            {/* Right, into the page — a bottom bubble would leave the viewport
              * on the rail's last item. */}
            <TooltipPopup side="right">
              {resolveEntityLabel(accountEntity)}
            </TooltipPopup>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={
                <MenuTrigger
                  render={
                    <Button
                      aria-label="Account menu"
                      className="rounded-full"
                      size="icon"
                      variant="ghost"
                    />
                  }
                >
                  <EntityTag
                    // avatarSize pins the circle at 24px inside the 32px icon
                    // button — the semantic sizes land on 20 or 32, neither of
                    // which fits the 40px header's rhythm.
                    avatarSize="sm"
                    display="visual"
                    entity={accountEntity}
                    interactive={false}
                    size="md"
                    presence
                  />
                </MenuTrigger>
              }
            />
            <TooltipPopup side="left">
              {resolveEntityLabel(accountEntity)}
            </TooltipPopup>
          </Tooltip>
        )}
        {/* Pinned at 288px, the DevTools-measured reference account-menu recipe:
          * 28px rows with 16px glyphs, 2px gaps BETWEEN rows (the gap-0.5 on
          * each group), 8px section insets on both axes around edge-to-edge
          * separators. The air comes from the gaps and section insets — not
          * taller rows. */}
        {/* The rail pins its trigger to the column's bottom, so the popup
          * rises off the top of that tile with its left edge on the tile's
          * (`side="top"` + `align="start"`) — a 288px panel right-aligned to a
          * 32px chip would hang off the viewport's left edge. The header
          * trigger drops from the top-right instead. */}
        <MenuPopup
          align={variant === "rail" ? "start" : "end"}
          className="w-72 max-w-72"
          padding="none"
          side={variant === "rail" ? "top" : "bottom"}
        >
          {/* padding="none" + per-section Box insets so the separators run edge
            * to edge. */}
          <Box paddingX="sm" paddingY="sm">
            <MenuGroup className="flex flex-col gap-0.5">
              {/* A MenuItem so the menu closes on press; the press is the same
                * action as the hover-revealed "Profile" affordance beside it.
                * Opens the right-side profile ISLAND — the settings page stays
                * reachable through the island's Edit action. */}
              <MenuItem
                aria-label="Open your profile"
                onClick={() => {
                  if (!currentUser?.userId) return;
                  closeDrawer();
                  openEntity("user", currentUser.userId, { via: "account_menu" });
                }}
              >
                <Box
                  align="center"
                  direction="row"
                  fill="width"
                  gap="sm"
                  group="menu-item"
                  justify="between"
                >
                  <EntityTag
                    entity={accountEntity}
                    interactive={false}
                    // The trigger this popup opened from wears a presence dot;
                    // the row naming that same account has to wear it too, or
                    // the state appears to change on open.
                    presence
                    size="md"
                    subtitle
                    subtitleStacked
                    weight="medium"
                  />
                  {/* Redundant "Profile" affordance for clarity. Its click
                    * bubbles to the MenuItem, so the row stays the single press
                    * action, and `tabIndex={-1}` keeps it out of the menu's
                    * keyboard navigation — it exists for its own hover state and
                    * the opaque popover fill that pops against the washed row.
                    * Revealed on row hover; `revealOnTouch` keeps it visible
                    * where hover never fires. */}
                  <Box hoverReveal="menu-item" revealOnTouch shrink={false}>
                    <Button
                      className="bg-popover"
                      shape="square"
                      size="xs"
                      tabIndex={-1}
                      variant="outline"
                    >
                      <Text size="xs" weight="medium">
                        Profile
                      </Text>
                    </Button>
                  </Box>
                </Box>
              </MenuItem>
            </MenuGroup>
          </Box>
          <MenuSeparator />
          <Box paddingX="sm" paddingY="sm">
            <MenuGroup className="flex flex-col gap-0.5">
              {/* Labeled "Settings" but lands on Preferences — this account
                * menu's settings entry point. The label is intentionally local
                * to this popover; "Settings" elsewhere may route differently.
                * On compact the door swaps the drawer to the settings pane
                * instead (`useSettingsDoors`). */}
              <MenuItem
                onClick={() =>
                  enterSettings(() => navigate("/settings/preferences"))
                }
              >
                <Icon color="muted" name="settings" size="md" />
                <Text>Settings</Text>
              </MenuItem>
              <ThemeSubMenu onNavigate={() => setAccountMenuOpen(false)} />
              {/* A reference of keys is nothing to a device that cannot
                * press them; ⌘/ still opens it the moment a keyboard speaks. */}
              {keyboardHints && (
                <MenuItem onClick={openKeyboardShortcuts}>
                  <Icon color="muted" name="keyboard" size="md" />
                  <Text>Keyboard shortcuts</Text>
                  <MenuShortcut>{KEYBOARD_SHORTCUTS_SHORTCUT_LABEL}</MenuShortcut>
                </MenuItem>
              )}
              <MenuItem onClick={openWhatsNew}>
                <Icon color="muted" name="gift" size="md" />
                <Text>What's new</Text>
                {/* MenuShortcut is a bare <kbd> with no render slot, so the count
                  * chip takes its `ms-auto` placement directly. */}
                {unseenCount > 0 && (
                  <Badge color="primary" tone="solid" className="ms-auto" size="sm">
                    {unseenCount}
                  </Badge>
                )}
              </MenuItem>
              <HelpSubMenu
                onAbout={() => setAboutOpen(true)}
                onSendFeedback={() => setFeedbackOpen(true)}
              />
            </MenuGroup>
          </Box>
          {isSuperuser && (
            <>
              <MenuSeparator />
              <Box paddingX="sm" paddingY="sm">
                <MenuGroup className="flex flex-col gap-0.5">
                  <MenuItem onClick={openAdmin}>
                    <Icon color="muted" name="shield-minus" size="md" />
                    <Text>Admin</Text>
                  </MenuItem>
                </MenuGroup>
              </Box>
            </>
          )}
          <MenuSeparator />
          <Box paddingX="sm" paddingY="sm">
            <MenuGroup className="flex flex-col gap-0.5">
              {canOpenTrash && (
                <MenuItem onClick={() => navigate("/settings/trash")}>
                  <Icon color="muted" name="trash" size="md" />
                  <Text>Trash</Text>
                </MenuItem>
              )}
              <MenuItem onClick={() => setLogoutConfirmOpen(true)}>
                <Icon color="muted" name="log-out" size="md" />
                <Text>Log out</Text>
                <MenuShortcut>{LOGOUT_SHORTCUT_LABEL}</MenuShortcut>
              </MenuItem>
            </MenuGroup>
          </Box>
        </MenuPopup>
      </Menu>
      <FeedbackDialog onOpenChange={setFeedbackOpen} open={feedbackOpen} />
      <AboutDialog onOpenChange={setAboutOpen} open={aboutOpen} />
      <LogoutConfirmDialog
        via="account_menu"
        onOpenChange={setLogoutConfirmOpen}
        open={logoutConfirmOpen}
      />
    </>
  );
}
